import { Clock } from "@phosphor-icons/react";
import { Calendar as ShadCalendar } from "../ui/calendar";
import { fmtEur } from "../../lib/api";
import { t, pickName } from "../../lib/i18n";
import { useBooking, STEP_DATA } from "./BookingContext";

function localeFor(locale) {
  if (locale === "en") return "en-GB";
  if (locale === "pt") return "pt-PT";
  if (locale === "ca") return "ca-ES";
  return "es-ES";
}

/**
 * Selector de fecha (calendario) + grid de slots. Soporta días bloqueados
 * según working_days y slots no disponibles (line-through + disabled).
 */
export default function SlotPicker() {
  const {
    data, service, total, addonsSubtotal, addonItems, addonQty,
    date, setDate, slots, setSlot, setStep, locale,
  } = useBooking();
  const tr = (k, params) => t(k, locale, params);
  if (!service) return null;
  const org = data?.org || {};

  return (
    <>
      <p className="text-xs uppercase tracking-[0.2em] text-gray-500">
        {pickName(service, locale)} · {service.duration_min || 30} {tr("min")} · <b>{fmtEur(total)}</b>
        {addonsSubtotal > 0 && (
          <span className="ml-1 text-[#0077B6]">
            ({addonItems.filter(a => (addonQty[a.id] || 0) > 0).length} extras)
          </span>
        )}
      </p>
      <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-2 text-[#1A1A1A]">
        {tr("elige.fecha")}
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 mt-6">
        <div className="bg-white border border-gray-200 rounded-md p-3 sm:p-4 pub-calendar-wrapper">
          <ShadCalendar
            mode="single"
            selected={date}
            onSelect={(d) => d && setDate(d)}
            disabled={(d) => {
              if (d < new Date(new Date().setHours(0,0,0,0))) return true;
              // B-006 fix: aceptar día si opening_hours_per_day[isoIdx] tiene tramos, aunque working_days legacy no lo incluya.
              // B-006 fix 2 (Jun 2026): aceptar claves legacy "mon"/"lun" además del numérico.
              const isoIdx = (d.getDay() + 6) % 7; // 0=Lun..6=Dom (igual que backend d.weekday())
              const perDay = org.opening_hours_per_day || {};
              const DAY_KEYS = [
                ["0", "mon", "monday", "lun", "lunes"],
                ["1", "tue", "tuesday", "mar", "martes"],
                ["2", "wed", "wednesday", "mie", "mié", "miércoles", "miercoles"],
                ["3", "thu", "thursday", "jue", "jueves"],
                ["4", "fri", "friday", "vie", "viernes"],
                ["5", "sat", "saturday", "sab", "sáb", "sábado", "sabado"],
                ["6", "sun", "sunday", "dom", "domingo"],
              ];
              for (const k of DAY_KEYS[isoIdx]) {
                const tramos = perDay[k];
                if (Array.isArray(tramos) && tramos.length > 0) return false;
              }
              return !org.working_days?.includes(isoIdx);
            }}
            data-testid="pub-calendar"
          />
        </div>
        <div className="bg-white border border-gray-200 rounded-md p-4 sm:p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#0077B6] font-semibold flex items-center gap-1.5">
            <Clock size={11} weight="bold" /> {tr("slots.libres")}
          </div>
          <div className="text-sm text-gray-700 mt-1">
            {date.toLocaleDateString(localeFor(locale), { weekday: "long", day: "numeric", month: "long" })}
          </div>
          <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[280px] overflow-auto pr-1" data-testid="pub-slots-grid">
            {slots.length === 0 && (
              <div className="col-span-full text-sm text-gray-500 py-8 text-center">
                <Clock size={24} className="mx-auto mb-2 text-[var(--ag-text-dim)]" />{tr("sin.slots")}
              </div>
            )}
            {slots.map((s) => (
              <button
                key={s.iso}
                disabled={!s.available}
                onClick={() => { setSlot(s); setStep(STEP_DATA); }}
                className={`h-12 rounded-md text-sm font-medium tabular-nums border-2 transition-all ${
                  s.available
                    ? "border-gray-200 text-[#1A1A1A] hover:border-[#FFB703] hover:bg-[#FFB703] hover:text-[#1A1A1A] active:scale-95 shadow-sm"
                    : "border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed line-through"
                }`}
                data-testid={`pub-slot-${s.time}`}
              >
                {s.time}
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
